import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
  MenuItem,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Divider
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Quiz as QuizIcon
} from '@mui/icons-material';
import api from '../services/api';

const CourseManager = () => {
  const [courses, setCourses] = useState([]);
  const [workloadForms, setWorkloadForms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [curriculumYearFilter, setCurriculumYearFilter] = useState('ALL');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    course_code: '',
    course_name: '',
    curriculum_year: '',
    cohort_prefixes: '',
    workload_form_id: '',
    description: '',
    requires_supervision: 1,
    requires_exam: 0
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWorkloadForms = async () => {
    try {
      const response = await api.get('/workload-forms');
      setWorkloadForms(response.data);
    } catch (err) {
      console.error('Failed to fetch workload forms:', err);
    }
  };

  useEffect(() => {
    fetchCourses();
    fetchWorkloadForms();
  }, []);

  const uniqueCurriculumYears = Array.from(
    new Set(courses.map((c: any) => c.curriculum_year).filter(Boolean))
  ).sort((a: any, b: any) => b - a);

  const filteredCourses = courses.filter((c: any) => {
    const matchYear = curriculumYearFilter === 'ALL' || String(c.curriculum_year) === String(curriculumYearFilter);
    const searchLower = searchTerm.toLowerCase();
    const matchSearch = 
      (c.course_code || '').toLowerCase().includes(searchLower) ||
      (c.course_name || '').toLowerCase().includes(searchLower) ||
      (c.cohort_prefixes || '').toLowerCase().includes(searchLower);
    return matchYear && matchSearch;
  });

  // Reset page when filtering
  useEffect(() => {
    setPage(0);
  }, [searchTerm, curriculumYearFilter]);

  const handleOpen = (course: any = null) => {
    if (course) {
      setEditingId(course.id);
      setFormData({
        course_code: course.course_code || '',
        course_name: course.course_name || '',
        curriculum_year: course.curriculum_year ? String(course.curriculum_year) : '',
        cohort_prefixes: course.cohort_prefixes || '',
        workload_form_id: course.workload_form_id || '',
        description: course.description || '',
        requires_supervision: course.requires_supervision !== undefined && course.requires_supervision !== null ? Number(course.requires_supervision) : 1,
        requires_exam: course.requires_exam ? Number(course.requires_exam) : 0
      });
    } else {
      setEditingId(null);
      setFormData({
        course_code: '',
        course_name: '',
        curriculum_year: '2567',
        cohort_prefixes: '',
        workload_form_id: '',
        description: '',
        requires_supervision: 1,
        requires_exam: 0
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingId(null);
  };

  const handleChange = (e: any) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      if (editingId) {
        await api.put(`/courses/${editingId}`, formData);
      } else {
        await api.post('/courses', formData);
      }
      handleClose();
      fetchCourses();
    } catch (err) {
      console.error(err);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูลรายวิชา');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('ยืนยันการลบรายวิชา?')) {
      try {
        await api.delete(`/courses/${id}`);
        fetchCourses();
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            จัดการรายวิชาฝึกปฏิบัติการสอน (Course Management)
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ผูกวิชากับแบบประเมินภาระงาน กำหนดเงื่อนไขการรับการนิเทศ และการสอบประมวลผลความรู้ตามหลักสูตร
          </Typography>
        </Box>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          เพิ่มรายวิชา
        </Button>
      </Box>

      {/* Filter and Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>ปีพัฒนาหลักสูตร</InputLabel>
              <Select
                value={curriculumYearFilter}
                label="ปีพัฒนาหลักสูตร"
                onChange={(e) => setCurriculumYearFilter(e.target.value)}
              >
                <MenuItem value="ALL">ทุกปีหลักสูตร (ทั้งหมด)</MenuItem>
                {uniqueCurriculumYears.map((year: any) => (
                  <MenuItem key={year} value={String(year)}>หลักสูตร พ.ศ. {year}</MenuItem>
                ))}
                {uniqueCurriculumYears.length === 0 && (
                  <>
                    <MenuItem value="2567">หลักสูตร พ.ศ. 2567</MenuItem>
                    <MenuItem value="2562">หลักสูตร พ.ศ. 2562</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 8 }}>
            <TextField 
              fullWidth 
              size="small" 
              placeholder="ค้นหารหัสวิชา, ชื่อวิชา หรือกลุ่มรหัสนักศึกษา (เช่น 65, 66)..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>รหัสวิชา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ชื่อรายวิชา</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>ปีหลักสูตร</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>กลุ่มรหัส นศ.</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>การรับการนิเทศ</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>การสอบประมวลผล</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>แบบประเมินภาระงานที่ผูก</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold' }}>การจัดการ</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCourses.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((course: any) => (
              <TableRow key={course.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{course.course_code}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{course.course_name}</TableCell>
                <TableCell>
                  {course.curriculum_year ? (
                    <Chip 
                      label={`พ.ศ. ${course.curriculum_year}`} 
                      size="small" 
                      color={course.curriculum_year >= 2567 ? "primary" : "default"}
                      variant="outlined" 
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {course.cohort_prefixes ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {course.cohort_prefixes.split(',').map((p: string) => (
                        <Chip key={p.trim()} label={`รหัส ${p.trim()}`} size="small" variant="filled" sx={{ fontSize: '0.75rem' }} />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="textSecondary">ทุกรหัส</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {Number(course.requires_supervision) === 1 ? (
                    <Chip 
                      icon={<CheckCircleIcon sx={{ fontSize: 16 }} />} 
                      label="ต้องรับการนิเทศ" 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip 
                      icon={<CancelIcon sx={{ fontSize: 16 }} />} 
                      label="ไม่ต้องรับการนิเทศ" 
                      size="small" 
                      variant="outlined"
                      sx={{ color: 'text.secondary', borderColor: 'grey.400' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {Number(course.requires_exam) === 1 ? (
                    <Chip 
                      icon={<QuizIcon sx={{ fontSize: 16 }} />} 
                      label="ต้องสอบประมวลผล" 
                      size="small" 
                      color="secondary" 
                      variant="filled"
                      sx={{ fontWeight: 600 }}
                    />
                  ) : (
                    <Chip 
                      label="ไม่ต้องสอบ" 
                      size="small" 
                      variant="outlined"
                      sx={{ color: 'text.secondary', borderColor: 'grey.300' }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {course.workload_form_title ? (
                    <Tooltip title={`คะแนนเต็ม ${course.workload_max_score || 0} คะแนน`}>
                      <Chip 
                        icon={<AssignmentIcon sx={{ fontSize: 16 }} />} 
                        label={`${course.workload_form_title} (${course.workload_max_score || 0} คะแนน)`} 
                        size="small" 
                        color="success" 
                        variant="outlined"
                      />
                    </Tooltip>
                  ) : (
                    <Chip label="ยังไม่ผูกแบบประเมิน" size="small" color="warning" variant="outlined" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="แก้ไข">
                    <IconButton color="primary" onClick={() => handleOpen(course)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="ลบ">
                    <IconButton color="error" onClick={() => handleDelete(course.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {filteredCourses.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>ไม่พบข้อมูลรายวิชา</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredCourses.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="รายการต่อหน้า:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} จาก ${count}`}
        />
      </TableContainer>

      {/* Course Add / Edit Modal */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingId ? 'แก้ไขรายวิชาฝึกปฏิบัติการสอน' : 'เพิ่มรายวิชาฝึกปฏิบัติการสอนใหม่'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="รหัสวิชา"
                name="course_code"
                placeholder="เช่น 1002101, 1004103"
                value={formData.course_code}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="ปีพัฒนาหลักสูตร (พ.ศ.)"
                name="curriculum_year"
                placeholder="เช่น 2562, 2567"
                type="number"
                value={formData.curriculum_year}
                onChange={handleChange}
                helperText="ปรับปรุงทุก 5 ปี"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="ชื่อรายวิชา"
                name="course_name"
                placeholder="เช่น การปฏิบัติการสอนในสถานศึกษา 1 (Internship 1)"
                value={formData.course_name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="กลุ่มรหัส 2 ตัวแรกของนักศึกษา (Cohort)"
                name="cohort_prefixes"
                placeholder="เช่น 62,63,64,65,66 หรือ 67,68,69 (คั่นด้วยจุลภาค)"
                value={formData.cohort_prefixes}
                onChange={handleChange}
                helperText="ระบุรหัส 2 ตัวแรกของรุ่นนักศึกษาที่เรียนรายวิชานี้ คั่นด้วยเครื่องหมายจุลภาค"
              />
            </Grid>
            
            {/* Supervison & Exam Options */}
            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1.5 }}>
                ⚙️ ข้อกำหนดการประเมินและการสำเร็จรายวิชา (Evaluation & Completion Policy)
              </Typography>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: Number(formData.requires_supervision) === 1 ? '#F0F9FF' : '#F9FAFB' }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1, color: '#0369A1' }}>
                    การรับการนิเทศ (Supervision Requirement)
                  </FormLabel>
                  <RadioGroup
                    name="requires_supervision"
                    value={formData.requires_supervision}
                    onChange={(e) => setFormData({ ...formData, requires_supervision: Number(e.target.value) })}
                  >
                    <FormControlLabel 
                      value={1} 
                      control={<Radio size="small" />} 
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>ต้องรับการนิเทศ</Typography>} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                      ต้องได้รับการประเมิน 3 ฝ่าย (ด้านวิชาชีพครู, ด้านวิชาเอก และครูพี่เลี้ยง เกณฑ์ผ่าน 60%)
                    </Typography>

                    <FormControlLabel 
                      value={0} 
                      control={<Radio size="small" />} 
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>ไม่ต้องรับการนิเทศ</Typography>} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
                      ยกเว้นการประเมิน 3 ฝ่าย ตัดสินผลการผ่านจากคะแนนภาระงานและการสอบ
                    </Typography>
                  </RadioGroup>
                </FormControl>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', bgcolor: Number(formData.requires_exam) === 1 ? '#FAF5FF' : '#F9FAFB' }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontWeight: 700, mb: 1, color: '#7E22CE' }}>
                    การสอบประมวลผลความรู้ (Comprehensive Exam)
                  </FormLabel>
                  <RadioGroup
                    name="requires_exam"
                    value={formData.requires_exam}
                    onChange={(e) => setFormData({ ...formData, requires_exam: Number(e.target.value) })}
                  >
                    <FormControlLabel 
                      value={1} 
                      control={<Radio size="small" color="secondary" />} 
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>ต้องสอบประมวลผลความรู้</Typography>} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block', mb: 1 }}>
                      ระบบจะนำคะแนนสอบที่ได้มาบวกเพิ่มในคะแนนประเมินภาระงานโดยอัตโนมัติตามคะแนนเต็มชุดข้อสอบ
                    </Typography>

                    <FormControlLabel 
                      value={0} 
                      control={<Radio size="small" color="secondary" />} 
                      label={<Typography variant="body2" sx={{ fontWeight: 600 }}>ไม่ต้องสอบประมวลผลความรู้</Typography>} 
                    />
                    <Typography variant="caption" color="textSecondary" sx={{ ml: 4, display: 'block' }}>
                      ประเมินผลเฉพาะตามแบบประเมินภาระงานปกติ ไม่มีการคิดคะแนนสอบ
                    </Typography>
                  </RadioGroup>
                </FormControl>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>แบบประเมินภาระงานที่ผูกกับรายวิชานี้</InputLabel>
                <Select
                  name="workload_form_id"
                  value={formData.workload_form_id}
                  label="แบบประเมินภาระงานที่ผูกกับรายวิชานี้"
                  onChange={handleChange}
                >
                  <MenuItem value=""><em>-- ไม่ผูกแบบประเมิน --</em></MenuItem>
                  {workloadForms.map((wf: any) => (
                    <MenuItem key={wf.id} value={wf.id}>
                      {wf.title} (หลักสูตร {wf.curriculum_year || '-'}, เต็ม {wf.total_max_score} คะแนน)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="คำอธิบายรายวิชา"
                name="description"
                value={formData.description}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose}>ยกเลิก</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">บันทึก</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CourseManager;
